import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Group,
  Settings,
  Storage,
  Security,
  Assessment,
  History,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();

  const adminSections = [
    {
      title: t('admin.users'),
      icon: <Group />,
      description: t('admin.usersDescription'),
      path: '/admin/users',
      permission: permissions.canManageUsers,
    },
    {
      title: t('admin.settings'),
      icon: <Settings />,
      description: t('admin.settingsDescription'),
      path: '/admin/settings',
      permission: permissions.canManageSettings,
    },
    {
      title: t('admin.storage'),
      icon: <Storage />,
      description: t('admin.storageDescription'),
      path: '/admin/storage',
      permission: permissions.canManageSettings,
    },
    {
      title: t('admin.security'),
      icon: <Security />,
      description: t('admin.securityDescription'),
      path: '/admin/security',
      permission: permissions.canManageSettings,
    },
    {
      title: t('admin.analytics'),
      icon: <Assessment />,
      description: t('admin.analyticsDescription'),
      path: '/admin/analytics',
      permission: permissions.canViewAuditLogs,
    },
    {
      title: t('admin.auditLogs'),
      icon: <History />,
      description: t('admin.auditLogsDescription'),
      path: '/admin/audit-logs',
      permission: permissions.canViewAuditLogs,
    },
    {
      title: t('admin.activityAudit'),
      icon: <Assessment />,
      description: t('admin.activityAuditDescription'),
      path: '/admin/activity',
      permission: permissions.canViewAuditLogs,
    },
    {
      title: t('admin.anomalies'),
      icon: <Security />,
      description: t('admin.anomaliesDescription'),
      path: '/admin/anomalies',
      permission: permissions.canViewAuditLogs,
    },
  ];

  const availableSections = adminSections.filter(section => section.permission);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('admin.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('admin.description')}
      </Typography>

      <Grid container spacing={3}>
        {availableSections.map((section) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={section.path}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s',
                },
              }}
              onClick={() => navigate(section.path)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      mr: 2,
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Typography variant="h6" component="h3">
                    {section.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {availableSections.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '40vh',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t('admin.noPermission')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminPage;
